import { useLayoutEffect, useRef } from "react";
import { IconPlus } from "@tabler/icons-react";

/**
 * A name field that can be filled from outside, by a suggestion. It takes the
 * focus, and while `isSuggested` holds it selects its whole value, so the
 * first key the reader presses replaces the suggestion. What the reader types
 * is never selected.
 */
export function SuggestedNameInput({
  value,
  isSuggested,
  placeholder,
  onChange,
  onSubmit,
}: {
  value: string;
  isSuggested: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  // Layout effect: the selection lands with the suggested value, before the
  // browser paints it unselected.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input === null) return;
    input.focus();
    if (isSuggested) input.select();
  }, [isSuggested, value]);

  return (
    <label className="flex w-full items-center gap-2 rounded-lg border-2 border-ds-mint-700 bg-white px-2.5 py-2">
      <IconPlus className="size-4 shrink-0 text-ds-slate-600" stroke={1.25} />
      <input
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        maxLength={80}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") onSubmit();
        }}
        className="min-w-0 flex-1 border-none bg-transparent p-0 text-ui-note text-ds-black outline-none selection:bg-ds-mint-100 placeholder:text-ds-slate-600"
      />
    </label>
  );
}
